import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import {
  AbstractComponent,
  ChatChannelTypeEnum,
  ChatCommandTypeEnum,
  ChatConversation,
  ChatSocketService,
  ChatUserDisplayMap,
} from 'lib-trend-core';
import { combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { conversationsSelector, userDisplaySelector } from '../../state/chat.selectors';

/** Dialog input — the message being forwarded plus a preview for the header. */
export interface ChatForwardDialogData {
  messageId: string;
  /** Short excerpt (or file name) shown as the forwarded content. */
  preview: string;
  /** Origin conversation — excluded from the destination list. */
  sourceChannelId: string | null;
}

/** Destination row of the picker. */
interface ChatForwardTarget {
  id: string;
  name: string;
  icon: string;
  type: ChatChannelTypeEnum;
}

/**
 * Internal Chat "Encaminhar" dialog (SPEC-2026-07-internal-chat, T-20 · RF-13).
 *
 * Destination picker over the store conversations — only the ones I am a member
 * of (`membership !== null`) and not archived, which mirrors the server rule
 * (MessageService.processForward resolves the destination with the full send
 * rules: active membership + not archived). The origin conversation is excluded.
 *
 * CONTRACT NOTE: the backend `FORWARD` payload carries no comment field
 * (`{ messageId, channelId | directUserId, clientMessageId }` — see
 * message.service.ts processForward). The optional comment is therefore sent as
 * a SEPARATE MESSAGE_SEND, chained on the forward's ack so the order in the
 * destination is always "forwarded message, then comment".
 */
@Component({
  templateUrl: './chat-forward-dialog.component.html',
  styleUrls: ['./chat-forward-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatForwardDialogComponent extends AbstractComponent implements OnInit {

  targets: ChatForwardTarget[] = [];
  filteredTargets: ChatForwardTarget[] = [];
  filterTerm = '';
  selectedId: string | null = null;
  comment = '';
  submitting = false;

  constructor(
    injector: Injector,
    private readonly dialogRef: MatDialogRef<ChatForwardDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: ChatForwardDialogData,
    private readonly store: Store,
    private readonly chatSocketService: ChatSocketService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select(conversationsSelector),
      this.store.select(userDisplaySelector),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([conversations, userDisplay]) => {
        this.targets = conversations
          .filter((conversation) => !!conversation.membership
            && !conversation.archived
            && conversation._id !== this.data?.sourceChannelId)
          .map((conversation) => ({
            id: conversation._id,
            name: this.resolveName(conversation, userDisplay),
            icon: this.resolveIcon(conversation.type),
            type: conversation.type,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        this.applyFilter();
      });
  }

  applyFilter(): void {
    const term = this.filterTerm.trim().toLowerCase();
    this.filteredTargets = term
      ? this.targets.filter((target) => target.name.toLowerCase().includes(term))
      : this.targets;
  }

  select(target: ChatForwardTarget): void {
    this.selectedId = this.selectedId === target.id ? null : target.id;
  }

  isSelected(target: ChatForwardTarget): boolean {
    return this.selectedId === target.id;
  }

  close(): void {
    this.dialogRef.close();
  }

  confirm(): void {
    if (this.submitting || !this.selectedId || !this.data?.messageId) return;

    const channelId = this.selectedId;
    const comment = this.comment.trim();
    this.submitting = true;

    // FORWARD is message-producing, so it would queue while offline by default —
    // but this dialog blocks on the ack, and a queued command never settles.
    // Refuse instead, so the user gets `chat.errors.offline` and can retry.
    this.chatSocketService.sendCommand(ChatCommandTypeEnum.FORWARD, {
      messageId: this.data.messageId,
      channelId,
    }, { queueWhenOffline: false })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Chained (not parallel) so the comment always lands AFTER the copy.
          if (comment) {
            this.chatSocketService.sendCommand(ChatCommandTypeEnum.MESSAGE_SEND, { channelId, content: comment })
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => { /* fan-out renders it in the destination */ },
                error: (ack) => this.alertService.error(this.resolveAckError(ack?.errorKey)),
              });
          }
          this.alertService.success(this.t('chat.forward.success'));
          this.dialogRef.close(true);
        },
        error: (ack) => {
          this.submitting = false;
          this.alertService.error(this.resolveAckError(ack?.errorKey));
        },
      });
  }

  trackTarget(_index: number, target: ChatForwardTarget): string {
    return target.id;
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private resolveName(conversation: ChatConversation, userDisplay: ChatUserDisplayMap): string {
    const currentUserId = this.getIDCurrentUser();
    switch (conversation.type) {
      case ChatChannelTypeEnum.DIRECT: {
        const otherId = (conversation.members ?? []).find((id) => id !== currentUserId) ?? currentUserId;
        return userDisplay[otherId]?.name ?? this.t('chat.bubble.directConversation');
      }
      case ChatChannelTypeEnum.GROUP: {
        if (conversation.name) return conversation.name;
        const names = (conversation.members ?? [])
          .filter((id) => id !== currentUserId)
          .map((id) => userDisplay[id]?.name)
          .filter((name): name is string => !!name);
        return names.length > 0 ? names.slice(0, 3).join(', ') : this.t('chat.bubble.groupConversation');
      }
      default:
        return conversation.name ?? this.t('chat.bubble.channelConversation');
    }
  }

  private resolveIcon(type: ChatChannelTypeEnum): string {
    switch (type) {
      case ChatChannelTypeEnum.DIRECT: return 'ri-user-3-line';
      case ChatChannelTypeEnum.GROUP: return 'ri-group-line';
      case ChatChannelTypeEnum.PRIVATE: return 'ri-lock-line';
      default: return 'ri-hashtag';
    }
  }

  /** Translates a backend/synthetic ack errorKey, falling back when unknown. */
  private resolveAckError(errorKey?: string): string {
    if (errorKey) {
      const translated = this.t(errorKey);
      if (translated !== errorKey) return translated;
    }
    return this.t('chat.errors.commandFailed');
  }
}
